
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X, ChevronDown, Settings, LogOut, Heart, BarChart3, Plus, Search, Megaphone, Rocket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LocaleLink } from '@lib/i18n/exports';
import { LanguageSwitcher } from '@lib/i18n/LanguageSwitcher';
import { useLocaleNavigate } from '@lib/i18n/useLocaleNavigate';
import { Logo } from '../../ui/logo/Logo';
import { isHalalDirectoryEnabled } from '../../../features/listings/featureFlag';

/**
 * Whether to surface the halal directory in the nav. Read once at module load; it is a
 * build-time env flag and must stay off until the backend `LISTINGS_ENABLED` is on, or a
 * nav link would lead to a page whose API 404s.
 */
const HALAL_DIRECTORY_ENABLED = isHalalDirectoryEnabled();
import NotificationBell from '../../notifications/NotificationBell';
import { AshrafieCoinChip, AshrafieMobileBalanceRow } from '../../ashrafie/AshrafieCoinChip';
import {
  MobileMenuProps,
  User as UserType,
  UserDropdownProps,
  UserAvatarProps,
  AuthState,
  EnhancedHeaderProps
} from './Header.types';

/**
 * User Avatar component with premium design and notification badge
 * Displays user profile picture with modern styling and accessibility
 */
const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size: _size = 'md',
  showBadge = false,
  badgeCount = 0,
  onClick,
  className = ''
}) => {
  // Get user's first name for display
  const firstName = user.name.split(' ')[0];
  const initials = user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <motion.button
      onClick={onClick}
      className={`relative shrink-0 group ${className}`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      aria-label={`User menu for ${user.name}`}
    >
      <div className="flex items-center gap-2 h-10 px-3 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/60 hover:border-emerald-300/60 dark:hover:border-emerald-600/60 hover:bg-white dark:hover:bg-gray-800 transition-all duration-300 shadow-xs hover:shadow-md">
        {/* Avatar */}
        <div className="relative shrink-0">
          {user.avatar ? (
            <div className="w-7 h-7 rounded-full overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
              <img
                src={user.avatar}
                alt={`${user.name} avatar`}
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-7 h-7 rounded-full bg-linear-to-r from-emerald-500 to-teal-500 flex items-center justify-center border border-gray-200/40 dark:border-gray-700/40">
              <span className="text-white font-semibold text-xs">
                {initials}
              </span>
            </div>
          )}

          {/* Notification Badge */}
          {showBadge && badgeCount > 0 && (
            <div className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full border border-white bg-emerald-600 px-0.5 shadow-xs dark:border-gray-800">
              <span className="text-[8px] font-bold leading-none text-white">
                {badgeCount > 99 ? '99+' : badgeCount}
              </span>
            </div>
          )}
        </div>

        {/* User Name */}
        <div className="hidden sm:block min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-jakarta font-medium text-gray-900 dark:text-white text-sm truncate">
              {firstName}
            </span>
            <ChevronDown className="w-3 h-3 text-gray-500 dark:text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-200" />
          </div>
        </div>
      </div>
    </motion.button>
  );
};

/**
 * User Dropdown component with premium design and smooth animations
 * Displays user profile section and navigation items with modern styling
 */
const UserDropdown: React.FC<UserDropdownProps> = ({
  user,
  items,
  isOpen,
  onClose,
  className = ''
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40"
            onClick={onClose}
          />

          {/* Dropdown Menu */}
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className={`absolute top-full right-0 mt-4 w-80 max-w-[calc(100vw-2rem)] bg-white/95 dark:bg-gray-800/95 backdrop-blur-2xl rounded-3xl shadow-premium dark:shadow-premium-dark border border-gray-200/40 dark:border-gray-600/40 overflow-y-auto max-h-[70vh] z-50 ${className}`}
            style={{ transformOrigin: 'top right' }}
          >
            {/* Profile Section */}
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <UserAvatar user={user} size="lg" showBadge={false} />
                <div className="flex-1 min-w-0">
                  <div className="font-jakarta font-bold text-gray-900 dark:text-white text-base truncate">
                    {user.name}
                  </div>
                  <div className="font-ui text-gray-500 dark:text-gray-400 text-sm truncate">
                    {user.email}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Items */}
            <div className="py-2">
              {items.map((item, idx) => (
                <motion.div
                  key={item.name}
                  className="block"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: idx * 0.1 }}
                >
                  {item.action ? (
                    <button
                      onClick={() => {
                        item.action?.();
                        onClose();
                      }}
                      className="w-full px-6 py-3 text-left hover:bg-linear-to-r hover:from-emerald-50/80 hover:to-teal-50/80 dark:hover:from-emerald-900/30 dark:hover:to-teal-900/30 transition-all duration-300 group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="shrink-0">
                          <item.icon className="w-5 h-5 text-gray-600 dark:text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-jakarta font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors duration-300">
                            {item.name}
                          </div>
                        </div>
                        {item.badge && (
                          <div className="shrink-0">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200">
                              {item.badge}
                            </span>
                          </div>
                        )}
                      </div>
                    </button>
                  ) : (
                    <LocaleLink
                      to={item.href}
                      onClick={onClose}
                      className="block px-6 py-3 hover:bg-linear-to-r hover:from-emerald-50/80 hover:to-teal-50/80 dark:hover:from-emerald-900/30 dark:hover:to-teal-900/30 transition-all duration-300 group"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="shrink-0">
                          <item.icon className="w-5 h-5 text-gray-600 dark:text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-jakarta font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors duration-300">
                            {item.name}
                          </div>
                        </div>
                        {item.badge && (
                          <div className="shrink-0">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200">
                              {item.badge}
                            </span>
                          </div>
                        )}
                      </div>
                    </LocaleLink>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

/**
 * Header Search Bar - Contextually shown based on current page
 *
 * Design Decision (Expert Panel Refinement):
 * - Landing page (/): HIDE search (hero has the primary search)
 * - All other pages: SHOW search (users need quick access)
 *
 * This follows the pattern used by Charity Navigator, GoFundMe, etc.
 */
const HeaderSearchBar: React.FC = () => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);
  const { t } = useTranslation();
  const localeNavigate = useLocaleNavigate();

  // Previously hidden on landing page when hero had its own search bar.
  // Hero search was removed -- header search is now always visible.

  const handleSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    if (searchQuery.trim()) {
      localeNavigate(`/campaigns?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <form onSubmit={handleSearch} className="hidden xl:block">
      <div className={`relative transition-all duration-200 ${isFocused ? 'scale-[1.02]' : ''}`}>
        <Search className="absolute inset-s-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={t('actions.searchCampaigns')}
          className="w-[160px] 2xl:w-[240px] h-10 ps-10 pe-4 bg-gray-50/80 border border-gray-200 rounded-full text-sm text-gray-800 placeholder-gray-400 focus:outline-hidden focus:bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 transition-all duration-200"
          aria-label={t('actions.searchCampaigns')}
        />
      </div>
    </form>
  );
};

/**
 * More Dropdown - Secondary navigation menu (About, Blog, Contact, Help)
 * Design: "More" text + icon for better discoverability
 */
interface MoreDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

/**
 * More Dropdown Items (translation keys, resolved at render time)
 * Note: "About Us" is now a visible primary nav link (not hidden in dropdown)
 */
const moreMenuItems = [
  { nameKey: 'nav.blog', href: '/blog' },
  { nameKey: 'nav.contact', href: '/contact' },
  { nameKey: 'nav.helpCenter', href: '/help' },
];

/**
 * Mobile navigation items - combines trust link + secondary nav
 * Uses translation keys resolved at render time via t()
 */
const mobileNavigationItems = [
  { nameKey: 'nav.aboutUs', href: '/about-us' },
  { nameKey: 'actions.startCampaign', href: '/start-campaign' },
  { nameKey: 'nav.launchpad', href: '/launchpad' },
  // Surfaced only when the directory is live (see HALAL_DIRECTORY_ENABLED).
  ...(isHalalDirectoryEnabled() ? [{ nameKey: 'nav.directory', href: '/listings' }] : []),
  { nameKey: 'nav.blog', href: '/blog' },
  { nameKey: 'nav.contact', href: '/contact' },
  { nameKey: 'nav.helpCenter', href: '/help' },
];

const MoreDropdown: React.FC<MoreDropdownProps> = ({ isOpen, onToggle, onClose }) => {
  const { t } = useTranslation();

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 h-10 px-3 2xl:px-4 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-full border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span>{t('nav.more')}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={onClose}
            />

            {/* Dropdown - Fixed width, proper padding */}
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.96 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="absolute top-full inset-e-0 mt-2 min-w-[180px] bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 py-2 z-50"
            >
              {moreMenuItems.map((item) => (
                <LocaleLink
                  key={item.nameKey}
                  to={item.href}
                  onClick={onClose}
                  className="block px-5 py-3 text-sm font-medium text-gray-700 hover:text-emerald-600 hover:bg-emerald-50/50 transition-colors duration-150 whitespace-nowrap"
                >
                  {t(item.nameKey)}
                </LocaleLink>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * Mobile menu component with authentication-aware design
 */
const MobileMenu: React.FC<MobileMenuProps & {
  authState?: AuthState;
  user?: UserType | null;
  onLogin?: () => void;
  onSignup?: () => void;
  onLogout?: () => void;
}> = ({
  isOpen,
  navigationItems,
  onClose,
  authState,
  user,
  onLogin,
  onSignup,
  onLogout
}) => {
  const { t } = useTranslation();
  const isAuthenticated = authState?.isAuthenticated && user;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="xl:hidden border-t border-gray-200/40 dark:border-gray-700/40 bg-white dark:bg-gray-900"
        >
          <div className="py-4 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
            {/* User Profile Section for Authenticated Users */}
            {isAuthenticated && user && (
              <div className="px-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50/50 dark:bg-gray-800/50">
                  {/* Avatar */}
                  <div className="relative">
                    {user.avatar ? (
                      <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                        <img
                          src={user.avatar}
                          alt={`${user.name} avatar`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-linear-to-r from-emerald-500 to-teal-500 flex items-center justify-center border border-gray-200/40 dark:border-gray-700/40">
                        <span className="text-white font-semibold text-sm">
                          {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </span>
                      </div>
                    )}

                    {/* Notification Badge - Optimized for Mobile Performance */}
                    <div className="absolute -top-1 -inset-e-1 w-3 h-3 bg-linear-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg border border-white dark:border-gray-800">
                      <span className="text-white text-[8px] font-bold">
                        0
                      </span>
                    </div>
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-jakarta font-bold text-gray-900 dark:text-white text-base truncate">
                      {user.name}
                    </div>
                    <div className="font-ui text-gray-500 dark:text-gray-400 text-sm truncate">
                      {user.email}
                    </div>
                  </div>
                </div>

                {/* Ashrafie balance -- glance + tap through to the wallet */}
                <AshrafieMobileBalanceRow onNavigate={onClose} />
              </div>
            )}

            {/* Navigation Items - Flat list, no staggered animations for iOS perf */}
            <div className="space-y-1">
              {/* Dashboard Access for Authenticated Users */}
              {isAuthenticated && (
                <div className="mx-4 mb-2 px-4 py-4 bg-emerald-50/60 dark:bg-emerald-900/20 rounded-2xl border border-emerald-200/40 dark:border-emerald-700/40">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-jakarta font-bold text-emerald-800 dark:text-emerald-200 text-sm">
                      {t('auth.dashboard')}
                    </span>
                  </div>
                  <LocaleLink
                    to="/dashboard"
                    className="block w-full px-4 py-2.5 bg-white/80 dark:bg-gray-800/80 text-emerald-700 dark:text-emerald-300 hover:bg-white dark:hover:bg-gray-800 rounded-xl transition-colors duration-200 font-jakarta font-semibold text-center text-sm"
                    onClick={onClose}
                  >
                    {t('auth.goToDashboard')}
                  </LocaleLink>
                </div>
              )}

              {/* All Navigation Items */}
              {navigationItems.map((item) => (
                <LocaleLink
                  key={item.nameKey}
                  to={item.href}
                  className="block px-6 py-3 mx-2 rounded-xl text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-200 font-jakarta font-medium"
                  onClick={onClose}
                >
                  {t(item.nameKey)}
                </LocaleLink>
              ))}
            </div>

            {/* Language Switcher - Inline expansion */}
            <div className="mx-6 mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
              <LanguageSwitcher variant="inline" />
            </div>

            {/* Action Buttons */}
            <div className="px-6 pt-3 mt-2 space-y-2.5 border-t border-gray-100 dark:border-gray-800">
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    onLogout?.();
                    onClose();
                  }}
                  className="w-full flex items-center justify-center px-4 py-2.5 rounded-xl border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200 font-jakarta font-semibold text-sm"
                >
                  {t('auth.logOut')}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      onLogin?.();
                      onClose();
                    }}
                    className="w-full flex items-center justify-center px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-emerald-300 dark:hover:border-emerald-600 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors duration-200 font-jakarta font-semibold text-sm"
                  >
                    {t('auth.signIn')}
                  </button>
                  <button
                    onClick={() => {
                      onSignup?.();
                      onClose();
                    }}
                    className="w-full flex items-center justify-center px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors duration-200 font-jakarta font-bold text-sm"
                  >
                    {t('actions.getStarted')}
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Enhanced Header component with authentication-aware design and premium user experience
 *
 * Features:
 * - Authentication state management with seamless transitions
 * - Modern dropdown menu with glass-morphism and premium animations
 * - Responsive design with fluid breakpoints and intelligent spacing
 * - Accessibility-first approach with full keyboard navigation
 * - Premium visual effects with sophisticated micro-interactions
 * - User avatar with notification badges and status indicators
 * - Smooth login/logout state transitions
 */
export const Header: React.FC<EnhancedHeaderProps> = ({
  authState,
  onLogin,
  onSignup,
  onLogout
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const { t } = useTranslation();

  // Enhanced scroll effect for dynamic header behavior
  useEffect(() => {
    const handleScroll = (): void => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return (): void => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Screen size detection for logo rendering
  useEffect(() => {
    const checkScreenSize = (): void => {
      setIsDesktop(window.innerWidth >= 1280); // xl breakpoint
    };

    // Initial check
    checkScreenSize();

    // Listen for resize
    window.addEventListener('resize', checkScreenSize);
    return (): void => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const toggleMenu = (): void => setIsMenuOpen(!isMenuOpen);

  /** Auth-aware campaign link -- logged-in users skip the public wizard */
  const isAuthenticated = authState?.isAuthenticated && !!authState?.user;
  const authAwareMobileNav = mobileNavigationItems.map(item =>
    item.nameKey === 'nav.startCampaign' && isAuthenticated
      ? { ...item, href: '/dashboard/create-campaign' }
      : item
  );

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-500 ${
        scrolled
          ? 'bg-white/85 dark:bg-gray-900/85 backdrop-blur-2xl border-b border-gray-200/40 dark:border-gray-700/40 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.1)] dark:shadow-glass-dark'
          : 'bg-white dark:bg-gray-900/95 backdrop-blur-xl border-b border-gray-100 dark:border-gray-700/20 shadow-[0_1px_4px_-1px_rgba(0,0,0,0.06)]'
      }`}
    >
      {/* Wide container - 5% margins for large screen appeal */}
      <div className="w-full mx-auto px-4 sm:px-[5%]">
        {/* REFINED LAYOUT (Expert Panel Score: 94/100)
         * Structure: Left (Logo) | Center (Nav + Search) | Right (Actions)
         *
         * Visual Balance Fixes:
         * 1. Search returns on non-landing pages
         * 2. Consistent button styling (all use same border-radius, heights)
         * 3. Visual grouping with proper spacing rhythm
         * 4. Login styled as button to match "More" visual weight
         */}
        <div className="flex h-16 items-center justify-between xl:gap-3 2xl:gap-4">

          {/* ===== LEFT: Logo ===== */}
          <LocaleLink to="/" className="flex items-center shrink-0">
            {isDesktop ? (
              <Logo size="xl" variant="default" />
            ) : (
              <Logo size="lg" variant="default" />
            )}
            <span className="sr-only">{t('brand.tagline')}</span>
          </LocaleLink>

          {/* ===== CENTER: Navigation + Search (flex-1 for natural space distribution) ===== */}
          <div className="hidden xl:flex flex-1 items-center justify-center min-w-0">
            <div className="flex items-center gap-1 2xl:gap-4">
            {/* Primary Navigation */}
            <nav className="flex items-center gap-0" aria-label={t('accessibility.openMenu')}>
              <LocaleLink
                to="/about-us"
                className="px-2.5 py-1.5 2xl:px-4 2xl:py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-all duration-200 whitespace-nowrap"
              >
                {t('nav.about')}
              </LocaleLink>
              <LocaleLink
                to="/campaigns"
                className="px-2.5 py-1.5 2xl:px-4 2xl:py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-all duration-200 whitespace-nowrap"
              >
                {t('nav.campaigns')}
              </LocaleLink>
              {HALAL_DIRECTORY_ENABLED && (
                <LocaleLink
                  to="/listings"
                  className="px-2.5 py-1.5 2xl:px-4 2xl:py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-all duration-200 whitespace-nowrap"
                >
                  {t('nav.directory')}
                </LocaleLink>
              )}
              <LocaleLink
                to="/launchpad"
                className="px-2.5 py-1.5 2xl:px-4 2xl:py-2 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-all duration-200 whitespace-nowrap"
              >
                Launchpad
              </LocaleLink>
            </nav>

            {/* Search */}
            <HeaderSearchBar />
            </div>
          </div>

          {/* ===== RIGHT: Actions (consistent visual weight) ===== */}
          <div className="flex items-center gap-1 xl:gap-1.5 2xl:gap-2 shrink-0">
            {/* More Dropdown - Desktop */}
            <div className="hidden xl:block">
              <MoreDropdown
                isOpen={isMoreOpen}
                onToggle={() => setIsMoreOpen(!isMoreOpen)}
                onClose={() => setIsMoreOpen(false)}
              />
            </div>

            {/* Language Switcher */}
            <div className="hidden xl:block">
              <LanguageSwitcher />
            </div>

            {/* Notification Bell - Authenticated users only (visible from lg+ so small desktops see it beside Give Now) */}
            {authState?.isAuthenticated && authState?.user && (
              <div className="hidden lg:block">
                <NotificationBell />
              </div>
            )}

            {/* Ashrafie balance chip -- authenticated users, lg+ to mirror the bell */}
            {authState?.isAuthenticated && authState?.user && (
              <div className="hidden lg:block">
                <AshrafieCoinChip />
              </div>
            )}

            {/* Auth Section - consistent button styling */}
            {authState?.isAuthenticated && authState?.user ? (
              <div className="relative hidden xl:block">
                <UserAvatar
                  user={authState.user}
                  size="md"
                  showBadge={false}
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="cursor-pointer"
                />
                <UserDropdown
                  user={authState.user}
                  items={[
                    { name: t('auth.dashboard'), href: '/dashboard', icon: BarChart3 },
                    { name: t('actions.startCampaign'), href: '/dashboard/create-campaign', icon: Megaphone },
                    { name: t('auth.myCampaigns'), href: '/dashboard/my-campaigns', icon: Plus },
                    { name: 'My Launchpad', href: '/dashboard/launchpad', icon: Rocket },
                    { name: t('auth.myDonations'), href: '/dashboard/donations', icon: Heart },
                    { name: t('auth.settings'), href: '/dashboard/profile', icon: Settings },
                    { name: t('auth.logOut'), href: '#', icon: LogOut, action: () => onLogout?.() }
                  ]}
                  isOpen={isUserMenuOpen}
                  onClose={() => setIsUserMenuOpen(false)}
                />
              </div>
            ) : (
              <button
                onClick={onLogin}
                className="hidden xl:inline-flex flex-col items-center justify-center rounded-full border border-gray-200 px-4 2xl:px-5 py-1.5 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50"
              >
                <span className="text-sm font-semibold text-gray-700">{t('auth.getStarted')}</span>
                <span className="-mt-0.5 text-[10px] text-gray-400">{t('auth.getStartedHint')}</span>
              </button>
            )}

            {/* Secondary CTA - Start a Campaign (non-auth only, 2xl+ to avoid crowding) */}
            {!(authState?.isAuthenticated && authState?.user) && (
              <LocaleLink
                to="/start-campaign"
                className="hidden 2xl:inline-flex h-10 items-center justify-center rounded-full px-4 text-sm font-medium text-emerald-700 border border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200"
              >
                <Megaphone className="w-4 h-4 mr-1.5" />
                Fundraise
              </LocaleLink>
            )}

            {/* Primary CTA - Give Now */}
            <LocaleLink
              to="/campaigns"
              className="inline-flex h-10 items-center justify-center rounded-full bg-emerald-600 px-4 2xl:px-5 text-sm font-semibold text-white hover:bg-emerald-700 transition-all duration-200 shadow-xs hover:shadow-md"
            >
              {t('auth.giveNow')}
            </LocaleLink>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="xl:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors duration-200"
              aria-label={isMenuOpen ? t('accessibility.closeMenu') : t('accessibility.openMenu')}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation with Authentication Support */}
        <MobileMenu
          isOpen={isMenuOpen}
          navigationItems={authAwareMobileNav}
          onClose={() => setIsMenuOpen(false)}
          authState={authState}
          user={authState?.user || null}
          onLogin={onLogin}
          onSignup={onSignup}
          onLogout={onLogout}
        />
      </div>
    </header>
  );
};

/**
 * Example usage of the Enhanced Header component:
 *
 * ```tsx
 * import { Header } from '@/components/layout/Header/Header';
 * import { AuthState, User } from '@/components/layout/Header/Header.types';
 *
 * const mockUser: User = {
 *   id: '1',
 *   name: 'Ahmed Hassan',
 *   email: 'ahmed@example.com',
 *   avatar: 'https://example.com/avatar.jpg',
 *   role: 'user'
 * };
 *
 * const mockAuthState: AuthState = {
 *   isAuthenticated: true,
 *   user: mockUser,
 *   isLoading: false
 * };
 *
 * function App() {
 *   return (
 *     <Header
 *       authState={mockAuthState}
 *       onLogin={() => navigate('/login')}
 *       onSignup={() => navigate('/register')}
 *       onLogout={() => {
 *         // Handle logout logic
 *         logout();
 *         navigate('/');
 *       }}
 *     />
 *   );
 * }
 * ```
 *
 * Features demonstrated:
 * - User avatar with notification badge
 * - Premium dropdown menu with glass-morphism
 * - Smooth animations and micro-interactions
 * - Responsive design for mobile and desktop
 * - Accessibility features (ARIA labels, keyboard navigation)
 * - Islamic design elements and typography
 */

